import {find, isEmpty, map, clone} from 'lodash';
import {Fragment, ReactNode, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {groupApi, rolesApi, userApi} from 'api';
import {
    ConfirmModal, Table, TableBody, TableRow, TableCell, Icon, Card, EmailLink, ActionButton, PredefinedNotifierType,
    UserInitials, FiltersDropdown, Select, FiltersWrapper, FiltersWrapperChildrenProps, InformationModal, Button, IconName
} from 'components';
import {ROUTE_PATH} from 'constants/routes';
import {UserContext, history} from 'context';
import {Choice} from 'enums';
import {useApiData, useObservable} from 'hooks';
import {Group, Option} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {accountPlanStore, getUserGroupsNames} from 'services';
import {BaseUserRole, OnlineStatus, UserParticipantRole, User, UserRole, UsersFilters} from 'types';
import {dateUtils, logger, userAuthUtils} from 'utils';
import {getParticipantName, formatDate, getRoleName, getRolesNames, getInSessionRoleNames, getInSessionRoleName} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.USERS.CURRENT_USERS;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;
const ONLINE_STATE_T_KEYS = TRANSLATION_KEYS.COMPONENTS.ONLINE_STATE;

const defaultFilters: UsersFilters = {
    onlineStatus: null,
    inSession: null,
    roleId: null,
    inSessionRole: null,
};

type Props = {
    initialUsersData: User[];
    usersLoading: boolean;
    users: User[];
    count: number;
    isMaxUsersCountExceeded: boolean;
    fetchUsers: (filters?: UsersFilters) => void
}

export const UsersList: React.FC<Props> = ({
    initialUsersData, usersLoading, users = [], count = 0, isMaxUsersCountExceeded = true, fetchUsers
}) => {
    const {t} = useTranslation();
    const [removeUserModalProps, setRemoveUserModalProps] = useState<{open: boolean, eid?: number}>();
    const [appliedFilters, setAppliedFilters] = useState(clone(defaultFilters));
    const [planWarningModalOpen, setPlanWarningModalOpen] = useState(false);
    const {settings} = useContext(UserContext);
    const {timeZone, dateFormat, timeFormat} = useMemo(() => settings.common, [settings]);
    const {limits} = useObservable(accountPlanStore);

    const {send: fetchGroups, data: groups = [], loading: groupsLoading} = useApiData<Group[]>(groupApi.getGroups);
    const {send: fetchRoles, data: roles = [], loading: rolesLoading} = useApiData<UserRole[]>(rolesApi.getRoles);
    const {send: removeUser, loading: removeUserLoading} = useApiData(userApi.removeUser, {
        notificationType: PredefinedNotifierType.Delete
    });
    const loading = useMemo(() => usersLoading || groupsLoading || rolesLoading, [usersLoading, groupsLoading, rolesLoading]);
    const planLimitReached = useMemo(() => limits?.maxUsers && limits?.maxUsers <= count, [count, limits]);

    const localUserEid = useMemo(() => userAuthUtils.getLocalUser().eid, []);
    const hasPersonalProfilePermissions = useMemo(() => userAuthUtils.getUserPermissions()?.account?.myProfile?.enabled, []);
    const hasBillingPagePermissions = useMemo(() => userAuthUtils.getUserPermissions()?.account?.billing?.enabled, []);
    const headers = useMemo(() => [
        {children: t(T_KEYS.TABLE.COLUMNS.NAME)},
        {children: t(T_KEYS.TABLE.COLUMNS.ASSIGNMENT)},
        {children: t(T_KEYS.TABLE.COLUMNS.EMAIL)},
        {children: t(T_KEYS.TABLE.COLUMNS.ROLES), className: 'tw-w-28'},
        ...(settings.featuresAvailability.assistantEnabled ?
            [{children: t(T_KEYS.TABLE.COLUMNS.IN_SESSION_ROLES), className: 'tw-w-28'}] : []),
        {children: t(T_KEYS.TABLE.COLUMNS.CREATED), className: '-lg:tw-w-28'},
        {children: t(T_KEYS.TABLE.COLUMNS.ACTIONS), className: 'tw-w-24'},
    ], [settings.featuresAvailability.assistantEnabled, t]);
    const canCreateUser = useMemo(() =>
        settings.registration.withPassword || settings.registration.withEmailConfirmation,
    [settings]);

    const onlineStatusSelectOptions: Option<OnlineStatus>[] = useMemo(() => [
        {id: OnlineStatus.Online, name: t(ONLINE_STATE_T_KEYS.ONLINE)},
        {id: OnlineStatus.Away, name: t(ONLINE_STATE_T_KEYS.AWAY)},
        {id: OnlineStatus.Offline, name: t(ONLINE_STATE_T_KEYS.OFFLINE)},
    ], [t]);

    const inSessionSelectOptions: Option<Choice>[] = useMemo(() => [
        {id: Choice.Yes, name: t(COMMON_T_KEYS.ALL.YES)},
        {id: Choice.No, name: t(COMMON_T_KEYS.ALL.NO)},
    ], [t]);

    const availableRoles = useMemo(() => [
        {id: BaseUserRole.Agent, name: BaseUserRole.Agent},
        {id: BaseUserRole.Admin, name: BaseUserRole.Admin},
        ...(roles || [])
    ], [roles]);

    const roleSelectOptions = useMemo(() =>
        map(availableRoles, ({id}) => ({id, name: getRoleName(t, id, availableRoles)})), [t, availableRoles]);

    const inSessionRolesOptions = useMemo(() =>
        map(UserParticipantRole, (inSessionRole) => ({id: inSessionRole, name: getInSessionRoleName(t, inSessionRole)})), [t]);

    const refetchUsers = useCallback(() => {
        fetchUsers(appliedFilters);
    }, [appliedFilters, fetchUsers]);

    const edit = useCallback((eid: number) => {
        const url = localUserEid === eid ? ROUTE_PATH.USER.ACCOUNT.MY_PROFILE :
            ROUTE_PATH.USER.ACCOUNT.EDIT_USER.replace(':eid', String(eid));
        history.push(url);
    }, [localUserEid]);
    const remove = useCallback((eid: number) => setRemoveUserModalProps({open: true, eid}), []);
    const add = useCallback(() => {
        if (planLimitReached) {
            setPlanWarningModalOpen(true);
        } else {
            history.push(ROUTE_PATH.USER.ACCOUNT.CREATE_USER);
        }
    }, [planLimitReached]);
    const closeRemoveUserConfirmation = useCallback(async (isConfirmed: boolean) => {
        const {eid} = removeUserModalProps;

        if (isConfirmed) {
            try {
                await removeUser(eid);
                refetchUsers();
            } catch (error: any) {
                logger.error(error);
            }
        }

        setRemoveUserModalProps({open: false});
    }, [removeUserModalProps, removeUser, refetchUsers]);
    const redirectToBillingPage = useCallback(() => history.push(ROUTE_PATH.USER.ACCOUNT.BILLING), []);

    const renderCardRow = useCallback((iconName: IconName, content: ReactNode) => (
        !isEmpty(content) &&
            <div className='tw-mt-2 tw-flex tw-items-start'>
                <Icon name={iconName} className='tw-ml-1.5' />
                <div className='tw-flex tw-justify-center tw-flex-col tw-min-h-5 tw-flex-1 tw-break-words tw-overflow-hidden tw-ml-4'>
                    {content}
                </div>
            </div>
    ), []);

    useEffect(() => {
        fetchRoles();
        fetchGroups();
    }, [fetchGroups, fetchRoles]);

    const renderCreationInfo = useCallback((user: User) => {
        const creator = find(initialUsersData, {eid: user.createdBy});
        const creatorName = getParticipantName(creator);
        return [
            creatorName && <div key='creatorName' className='tw-mb-1'>{creatorName}</div>,
            user.createdAt &&
                <div key='creationDate'>
                    {formatDate(user.createdAt, timeZone, dateUtils.getDateTimeFormat(dateFormat, timeFormat))}
                </div>
        ];
    }, [dateFormat, initialUsersData, timeFormat, timeZone]);

    useEffect(() => {
        refetchUsers();
    }, [refetchUsers]);

    return (
        <div>
            <div className='tw-mb-4'>{t(T_KEYS.DESCRIPTION)}</div>
            <div className='tw-flex -md:tw-flex-col tw-justify-end tw-items-start md:tw-items-center tw-font-medium tw-mb-4'>
                {!loading &&
                    <div className='tw-mr-auto -md:tw-order-2'>
                        {`${users.length} ${t(T_KEYS.OF)} ${count} ${t(T_KEYS.COUNT_TITLE)}`}
                    </div>}
                <div className='tw-flex tw-flex-wrap -md:tw-order-1 -md:tw-mb-2'>
                    <FiltersWrapper
                        defaultFilters={defaultFilters}
                        filters={appliedFilters}
                        onSubmit={setAppliedFilters}
                    >
                        {({filters, onFiltersChange, ...rest}: FiltersWrapperChildrenProps<UsersFilters>) => (
                            <FiltersDropdown className='tw-mr-2 -sm:tw-my-2' {...rest}>
                                <Fragment>
                                    <Select
                                        placeholder={t(COMMON_T_KEYS.SESSION.LABELS.STATUS)}
                                        options={onlineStatusSelectOptions}
                                        noneOption
                                        valueAs='id'
                                        value={filters.onlineStatus}
                                        onChange={(id) => onFiltersChange({onlineStatus: id})}
                                    />
                                    <Select
                                        placeholder={t(COMMON_T_KEYS.SESSION.STATUS.IN_SESSION)}
                                        options={inSessionSelectOptions}
                                        noneOption
                                        valueAs='id'
                                        value={filters.inSession}
                                        onChange={(id) => onFiltersChange({inSession: id})}
                                    />
                                    <Select
                                        placeholder={t(COMMON_T_KEYS.SESSION.LABELS.ROLE)}
                                        options={roleSelectOptions}
                                        noneOption
                                        valueAs='id'
                                        value={filters.roleId}
                                        onChange={(id) => onFiltersChange({roleId: id})}
                                    />
                                    {settings.featuresAvailability.assistantEnabled &&
                                        <Select
                                            placeholder={t(COMMON_T_KEYS.SESSION.LABELS.IN_SESSION_ROLE)}
                                            options={inSessionRolesOptions}
                                            noneOption
                                            valueAs='id'
                                            value={filters.inSessionRole}
                                            onChange={(id) => onFiltersChange({inSessionRole: id})}
                                        />}
                                </Fragment>
                            </FiltersDropdown>
                        )}
                    </FiltersWrapper>
                    {canCreateUser &&
                        <ActionButton
                            className='-sm:tw-my-2'
                            action='create'
                            disabled={isMaxUsersCountExceeded && !planLimitReached}
                            onClick={add}
                        >
                            {t(T_KEYS.BUTTONS.CREATE)}
                        </ActionButton>}
                </div>
            </div>
            <Table
                headers={headers}
                noData={!users.length}
                className='tw-hidden md:tw-table'
                loading={loading}
            >
                <TableBody>
                    {map(users, (user: User) => (
                        <TableRow key={user.email}>
                            <TableCell>
                                <div className='tw-flex tw-items-center tw-text-break-word'>
                                    <UserInitials
                                        user={user}
                                        className='tw-mr-2'
                                        showUnactivated
                                    />
                                    {getParticipantName(user)}
                                </div>
                            </TableCell>
                            <TableCell>{getUserGroupsNames(user.groups, groups)}</TableCell>
                            <TableCell><EmailLink email={user.email} /></TableCell>
                            <TableCell>{getRolesNames(t, user.roles, roles)}</TableCell>
                            {settings.featuresAvailability.assistantEnabled &&
                                <TableCell>{getInSessionRoleNames(t, user.inSessionRoles)}</TableCell>}
                            <TableCell>{renderCreationInfo(user)}</TableCell>
                            <TableCell>
                                {(user.eid !== localUserEid || hasPersonalProfilePermissions) &&
                                    <div className='tw-flex'>
                                        <Icon name='edit' color='primary' button group onClick={() => edit(user.eid)} />
                                        <Icon name='delete' color='alert' button group onClick={() => remove(user.eid)} />
                                    </div>}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <div className='tw-block md:tw-hidden'>
                {map(users, (user: User) => {
                    const groupNames = getUserGroupsNames(user.groups, groups);
                    const rolesNames = getRolesNames(t, user.roles, roles);
                    const inSessionRoleNames = getInSessionRoleNames(t, user.inSessionRoles);
                    return (
                        <Card
                            key={user.email}
                            className='tw-mb-3 tw-text-sm'
                        >
                            <div className='tw-flex tw-justify-between tw-mb-3'>
                                <div className='tw-flex tw-items-center tw-font-bold'>
                                    <UserInitials
                                        user={user}
                                        className='tw-mr-2'
                                        showUnactivated
                                    />
                                    {getParticipantName(user)}
                                </div>
                                {(user.eid !== localUserEid || hasPersonalProfilePermissions) &&
                                    <div className='tw-flex'>
                                        <Icon name='edit' color='primary' button group onClick={() => edit(user.eid)} />
                                        <Icon name='delete' color='alert' button group onClick={() => remove(user.eid)} />
                                    </div>}
                            </div>
                            {renderCardRow('mail', <EmailLink email={user.email} className='tw-text-sm' />)}
                            {renderCardRow('assignment', groupNames)}
                            {renderCardRow('permission', rolesNames)}
                            {/* TODO: ICON */}
                            {renderCardRow('rocket', inSessionRoleNames)}
                            {renderCardRow('clock', renderCreationInfo(user))}
                        </Card>
                    );
                })}
            </div>

            <ConfirmModal
                open={removeUserModalProps?.open}
                okDisabled={removeUserLoading}
                title={t(T_KEYS.CONFIRM_REMOVE_DIALOG.TITLE)}
                onClose={closeRemoveUserConfirmation}
            />

            {planLimitReached &&
                <InformationModal
                    open={planWarningModalOpen}
                    title={t(T_KEYS.PLAN_WARNING_MODAL.TITLE)}
                    fullWidth
                    maxWidth='xs'
                    onClose={() => setPlanWarningModalOpen(false)}
                >
                    <div className='tw-mb-2'>{t(T_KEYS.PLAN_WARNING_MODAL.MESSAGE_LINE_1)}</div>
                    <div>{t(T_KEYS.PLAN_WARNING_MODAL.MESSAGE_LINE_2)}</div>
                    {hasBillingPagePermissions &&
                        <div className='tw-text-center tw-mt-8'>
                            <Button color='primary' onClick={redirectToBillingPage}>{t(T_KEYS.PLAN_WARNING_MODAL.GO_TO_BILLING)}</Button>
                        </div>}
                </InformationModal>}
        </div>
    );
};
